/** InsCoder WANG.J 2026-08-27 B + LOAD MORE pagination (Figma 1:1) custom logic + listing state & scroll preservation **/
(function () {
  const CARD_SELECTOR = 'product-card, .product-list__promo';
  const STORAGE_PREFIX = 'cust-load-more:';

  // Only restore the listing on back/forward navigation (or a reload). A fresh
  // navigation into the page (clicking a link, typing the URL) must start at
  // the top and must NOT pick up a stale snapshot from the same tab session.
  const navEntry = performance.getEntriesByType?.('navigation')?.[0];
  const navType = navEntry
    ? navEntry.type
    : (performance.navigation
        ? ({ 0: 'navigate', 1: 'reload', 2: 'back_forward' })[performance.navigation.type] || 'navigate'
        : 'navigate');
  const isRestorableNavigation = navType === 'back_forward' || navType === 'reload';

  /* --------------------------------------------------------------------------
   * Snapshot helpers
   * The snapshot records how far the customer has loaded the list (loaded page
   * count, total products, scroll position) so that a back-navigation which
   * falls back to a full reload (bfcache evicted) can restore the same state.
   * Filters / sort / search query already live in the URL, so they do not need
   * to be persisted here.
   * ------------------------------------------------------------------------ */
  const getSnapshotKey = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    url.hash = '';
    return STORAGE_PREFIX + url.pathname + url.search;
  };

  const readSnapshot = () => {
    try {
      const raw = sessionStorage.getItem(getSnapshotKey());
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  };

  const writeSnapshot = (snapshot) => {
    try {
      sessionStorage.setItem(getSnapshotKey(), JSON.stringify(snapshot));
    } catch (error) {
      // Storage full or unavailable: state preservation degrades gracefully.
    }
  };

  /* --------------------------------------------------------------------------
   * Shared DOM helpers (used by both the click flow and the restore flow)
   * ------------------------------------------------------------------------ */
  const getProductList = (sectionId) =>
    document.getElementById(`shopify-section-${sectionId}`)?.querySelector('product-list') ||
    document.querySelector('product-list');

  const appendCardsFromDoc = (doc, sectionId) => {
    const newSection = doc.getElementById(`shopify-section-${sectionId}`) || doc.querySelector('.shopify-section');
    const newProductList = newSection?.querySelector('product-list');
    if (!newProductList) return { appendedCards: [], newButtonHref: null };

    const appendedCards = Array.from(newProductList.querySelectorAll(CARD_SELECTOR));
    const newButtonHref = newSection.querySelector('[data-load-more-button]')?.href || null;
    return { appendedCards, newButtonHref };
  };

  const updateProductCount = (container, productList) => {
    const countEl = container.querySelector('[data-load-more-count]');
    if (!countEl || !container.dataset.countTemplate) return;
    const loadedCount = productList.querySelectorAll('product-card').length;
    countEl.textContent = container.dataset.countTemplate
      .replace('__loaded__', String(loadedCount))
      .replace('__total__', String(container.dataset.totalProducts));
  };

  const revealAppendedCards = (cards) => {
    const revealCards = cards.filter((card) => card.matches('[reveal-on-scroll="true"]'));
    if (revealCards.length === 0) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // Safety net: make the cards visible if the animation is skipped (e.g. reduced motion)
      revealCards.forEach((card) => { card.style.opacity = '1'; });
      return;
    }

    const staggerDelay = window.themeVariables?.settings?.staggerProducts ? 50 : 0;
    revealCards.forEach((card, index) => {
      card.animate(
        [
          { opacity: 0, transform: 'translateY(20px)' },
          { opacity: 1, transform: 'translateY(0)' }
        ],
        { duration: 150, delay: index * staggerDelay, easing: 'ease-in-out', fill: 'both' }
      );
    });
  };

  const updateButtonState = (container, newButtonHref) => {
    const button = container.querySelector('[data-load-more-button]');
    if (!button) return;
    if (newButtonHref) {
      button.href = newButtonHref;
    } else {
      button.remove();
    }
  };

  /* --------------------------------------------------------------------------
   * Scroll tracking
   * Keeps the persisted snapshot's scrollY up to date so the restore lands on
   * the exact position the customer had scrolled to (not only the position at
   * the moment of the last "load more" click).
   * ------------------------------------------------------------------------ */
  let activeSnapshot = null;
  let activeSnapshotKey = null;
  let scrollTickPending = false;
  // Scroll events during the initial page load (browser scroll restoration,
  // hash anchors) carry a position from before the restore flow has run —
  // ignore them until pageshow so they cannot overwrite the saved snapshot.
  let scrollTrackingActive = false;

  const invalidateSnapshot = () => {
    activeSnapshot = null;
    activeSnapshotKey = null;
  };

  const saveActiveSnapshot = (snapshot) => {
    activeSnapshot = snapshot;
    activeSnapshotKey = getSnapshotKey();
    writeSnapshot(snapshot);
  };

  window.addEventListener('scroll', () => {
    if (!scrollTrackingActive || scrollTickPending) return;
    if (getSnapshotKey() !== activeSnapshotKey) {
      // The URL changed (e.g. filters / sort / search were updated), so this
      // snapshot no longer describes the current listing — drop it.
      invalidateSnapshot();
    }
    scrollTickPending = true;
    requestAnimationFrame(() => {
      scrollTickPending = false;
      const container = document.querySelector('[data-load-more-container]');
      if (!container) return;
      // Track the scroll position even when the customer has not clicked
      // "load more" yet, so a back-navigation restores the position too.
      if (!activeSnapshot) {
        activeSnapshot = {
          lastPage: 1,
          total: Number(container.dataset.totalProducts) || 0,
          sectionId: container.dataset.sectionId
        };
        activeSnapshotKey = getSnapshotKey();
      }
      activeSnapshot.scrollY = window.scrollY;
      writeSnapshot(activeSnapshot);
    });
  }, { passive: true });

  window.addEventListener('popstate', invalidateSnapshot);
  document.addEventListener('facet:update', invalidateSnapshot);

  /* --------------------------------------------------------------------------
   * Restore flow
   * Runs on a fresh load (back-navigation when the bfcache is unavailable, or a
   * plain reload). Re-fetches pages 2..N and re-appends them so the listing and
   * the product count look exactly as the customer left them, then restores the
   * scroll position.
   * ------------------------------------------------------------------------ */
  let restoreAttempted = false;

  // Original scroll restoration mode, captured before we may take over with
  // 'manual'. It is handed back on every early exit and in the finally cleanup
  // so the browser's default scroll restoration is restored once our flow ends.
  const originalScrollRestoration = history.scrollRestoration;

  // Mainstream approach: take over scroll restoration as early as possible,
  // BEFORE the browser's native scroll restoration runs. With only the first
  // page rendered, the browser would clamp the scroll to the bottom of the
  // short page. If a restorable snapshot exists (the customer had loaded more
  // pages before leaving), we hand the scroll to our own restore flow.
  const pendingSnapshot = readSnapshot();
  if (isRestorableNavigation && pendingSnapshot && 'scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  const restoreSnapshot = async () => {
    if (restoreAttempted) return;
    restoreAttempted = true;

    const currentPage = parseInt(new URLSearchParams(window.location.search).get('page') || '1', 10);
    if (currentPage > 1) {
      // URL-driven pagination is active; the snapshot does not apply. Hand the
      // manual takeover back so the browser keeps its default behavior.
      if ('scrollRestoration' in history) history.scrollRestoration = originalScrollRestoration;
      return;
    }

    const snapshot = readSnapshot();
    if (!isRestorableNavigation || !snapshot) {
      if ('scrollRestoration' in history) history.scrollRestoration = originalScrollRestoration;
      return;
    }

    const container = document.querySelector('[data-load-more-container]');
    const productList = container && getProductList(container.dataset.sectionId);
    if (!container || !productList || container.dataset.sectionId !== snapshot.sectionId) {
      if ('scrollRestoration' in history) history.scrollRestoration = originalScrollRestoration;
      return;
    }

    // Scroll-only snapshot (the customer never clicked "load more"): the first
    // page is already rendered by the server, so just restore the position.
    if (snapshot.lastPage <= 1) {
      if ('scrollRestoration' in history) history.scrollRestoration = originalScrollRestoration;
      requestAnimationFrame(() => {
        window.scrollTo(0, snapshot.scrollY || 0);
      });
      return;
    }

    // Loading state: keep the load-more button disabled while products are restored
    container.dataset.loading = 'true';
    container.classList.add('is-loading');
    const button = container.querySelector('[data-load-more-button]');
    if (button) button.setAttribute('aria-busy', 'true');

    // scrollRestoration was already set to 'manual' on script load (see the
    // early takeover above); the finally block hands it back afterwards.
    try {
      // Use the load-more button's href as the base URL: on the search page it
      // carries `type=product` (plus any active filters/sort), which the bare
      // location URL lacks. Without `type=product`, Shopify renders a
      // multi-type search response where page 2+ yields no product cards.
      const baseUrl = new URL(button?.href || window.location.href);
      baseUrl.searchParams.set('section_id', snapshot.sectionId);

      // Build the URLs for pages 2..N first, then fetch them in parallel
      // (Promise.allSettled) so a large lastPage does not queue requests one
      // after another. Failing pages are skipped and the restored list
      // degrades gracefully to the pages that did succeed.
      const pageUrls = [];
      for (let page = 2; page <= snapshot.lastPage; page++) {
        const pageUrl = new URL(baseUrl);
        pageUrl.searchParams.set('page', page);
        pageUrls.push({ page, url: pageUrl });
      }

      const results = await Promise.allSettled(
        pageUrls.map(async ({ page, url }) => {
          const response = await fetch(url.toString(), {
            headers: { 'Accept': 'text/html' },
            credentials: 'same-origin'
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const { appendedCards, newButtonHref: hrefFromDoc } = appendCardsFromDoc(doc, snapshot.sectionId);
          if (appendedCards.length === 0) throw new Error('No product cards in response');
          return { page, appendedCards, newButtonHref: hrefFromDoc };
        })
      );

      // Collect cards page by page (keeps the visual order of the listing)
      const restoredCards = [];
      for (const result of results) {
        if (result.status === 'fulfilled') {
          restoredCards.push(...result.value.appendedCards);
        } else {
          console.warn('[LOAD MORE] Failed to restore a page:', result.reason);
        }
      }
      if (restoredCards.length === 0) throw new Error('No product cards restored');

      // The load-more button points at the page after the last successfully
      // restored page, so the customer can keep loading from where we stopped
      let newButtonHref = null;
      for (let i = results.length - 1; i >= 0; i--) {
        if (results[i].status === 'fulfilled') {
          newButtonHref = results[i].value.newButtonHref;
          break;
        }
      }

      productList.append(...restoredCards);

      updateProductCount(container, productList);
      revealAppendedCards(restoredCards);
      updateButtonState(container, newButtonHref);

      // Keep the snapshot accurate for further interactions
      saveActiveSnapshot({
        lastPage: snapshot.lastPage,
        total: Number(container.dataset.totalProducts) || snapshot.total,
        sectionId: snapshot.sectionId,
        scrollY: snapshot.scrollY // the intended position, not the browser-clamped scrollY
      });

      // Restore the scroll position once the page has its final height
      requestAnimationFrame(() => {
        window.scrollTo(0, snapshot.scrollY || 0);
      });
    } catch (error) {
      console.error('[LOAD MORE] Failed to restore listing state:', error);
    } finally {
      if ('scrollRestoration' in history) history.scrollRestoration = originalScrollRestoration;
      container.dataset.loading = 'false';
      container.classList.remove('is-loading');
      if (button) button.removeAttribute('aria-busy');
    }
  };

  /* --------------------------------------------------------------------------
   * Click flow
   * ------------------------------------------------------------------------ */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-load-more-button]');
    if (!button) return;

    event.preventDefault();

    const container = button.closest('[data-load-more-container]');
    if (!container || container.dataset.loading === 'true') return;

    // Loading state: prevent multiple requests while products are loading
    container.dataset.loading = 'true';
    container.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    try {
      const sectionId = container.dataset.sectionId;
      const pageLoaded = parseInt(new URL(button.href).searchParams.get('page') || '1', 10);

      const url = new URL(button.href);
      url.searchParams.set('section_id', sectionId);

      const response = await fetch(url.toString(), {
        headers: { 'Accept': 'text/html' },
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const { appendedCards, newButtonHref } = appendCardsFromDoc(doc, sectionId);
      if (appendedCards.length === 0) throw new Error('No product cards in response');

      const productList = getProductList(sectionId);
      productList.append(...appendedCards);

      // Update product count after products have loaded (loaded / total)
      updateProductCount(container, productList);

      // Reveal newly appended cards (parity with the theme reveal-on-scroll animation)
      revealAppendedCards(appendedCards);

      // Hide the button once all available products have been displayed
      updateButtonState(container, newButtonHref);

      // Capture the listing state so a later back-navigation (falling back to a
      // reload when the bfcache is unavailable) can restore it
      saveActiveSnapshot({
        lastPage: pageLoaded,
        total: Number(container.dataset.totalProducts) || 0,
        sectionId,
        scrollY: window.scrollY
      });
    } catch (error) {
      console.error('[LOAD MORE] Failed to load more products:', error);
    } finally {
      container.dataset.loading = 'false';
      container.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  });

  /* --------------------------------------------------------------------------
   * Page lifecycle
   * - persisted  => the bfcache restored the DOM and the scroll as-is; just
   *   clear any half-finished loading state left by an in-flight request and
   *   re-arm the scroll tracking.
   * - fresh load => restore the listing from the snapshot (bfcache evicted or
   *   plain reload).
   * ------------------------------------------------------------------------ */
  window.addEventListener('pageshow', (event) => {
    scrollTrackingActive = true;
    if (event.persisted) {
      document.querySelectorAll('[data-load-more-container]').forEach((container) => {
        container.dataset.loading = 'false';
        container.classList.remove('is-loading');
        const btn = container.querySelector('[data-load-more-button]');
        if (btn) btn.removeAttribute('aria-busy');
      });

      // The bfcache restored the DOM as-is. If we took over scroll restoration
      // early (scrollRestoration = 'manual'), the browser will NOT restore the
      // scroll position — do it ourselves from the snapshot.
      const snap = activeSnapshot || readSnapshot();
      if (snap) {
        activeSnapshot = snap;
        activeSnapshotKey = getSnapshotKey();
        if (snap.scrollY) window.scrollTo(0, snap.scrollY);
      }
      return;
    }
    restoreSnapshot();
  });

  window.addEventListener('pagehide', () => {
    // Final flush of the scroll position right before the page is frozen
    // (bfcache) or navigated away — the throttled scroll listener may have
    // missed the last frame.
    if (activeSnapshot) {
      activeSnapshot.scrollY = window.scrollY;
      writeSnapshot(activeSnapshot);
    }
  });
})();
/** InsCoder WANG.J 2026-08-27 E + LOAD MORE pagination (Figma 1:1) custom logic **/
